// src/pages/RolesPage.js
// ============================================================
//  GFM3 — Page Rôles & Droits d'accès
//  Gestion complète des rôles, permissions CRUD et affectation
//  des utilisateurs — calée sur le style de UtilisateursPage.js
// ============================================================

import React, { useEffect, useState, useCallback, useRef } from 'react';
import api from '../services/api';

// ── Constantes ────────────────────────────────────────────────

const MODULES_LABELS = {
  employes:     'Employés',
  contrats:     'Contrats',
  conges:       'Congés & Absences',
  paie:         'Paie & Rémunérations',
  parametres:   'Paramètres',
  utilisateurs: 'Utilisateurs',
  rapports:     'Rapports',
  pointage:     'Pointage',
};

const MODULES_ORDRE = [
  'employes', 'contrats', 'conges', 'paie',
  'pointage', 'rapports', 'parametres', 'utilisateurs',
];

const ACTIONS_PAR_MODULE = {
  employes:     ['lire', 'creer', 'modifier', 'supprimer'],
  contrats:     ['lire', 'creer', 'modifier', 'supprimer'],
  conges:       ['lire', 'creer', 'modifier', 'supprimer', 'valider'],
  paie:         ['lire', 'creer', 'modifier', 'valider', 'exporter'],
  parametres:   ['lire', 'modifier'],
  utilisateurs: ['lire', 'creer', 'modifier', 'supprimer'],
  rapports:     ['lire', 'exporter'],
  pointage:     ['lire', 'creer', 'modifier', 'supprimer'],
};

const TOUTES_ACTIONS = ['lire', 'creer', 'modifier', 'supprimer', 'valider', 'exporter'];

const ACTIONS_LABELS = {
  lire:      'Lire',
  creer:     'Créer',
  modifier:  'Modifier',
  supprimer: 'Supprimer',
  valider:   'Valider',
  exporter:  'Exporter',
};

const COULEURS_PRESET = [
  { couleur: '#4a148c', bg: '#f3e5f5' },
  { couleur: '#b71c1c', bg: '#ffebee' },
  { couleur: '#1565C0', bg: '#e3f2fd' },
  { couleur: '#e65100', bg: '#fff3e0' },
  { couleur: '#2e7d32', bg: '#e8f5e9' },
  { couleur: '#00838f', bg: '#e0f7fa' },
  { couleur: '#4527a0', bg: '#ede7f6' },
  { couleur: '#546e7a', bg: '#eceff1' },
];

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

// ── Styles partagés ───────────────────────────────────────────

const inputStyle = {
  border: '1px solid #cfd8dc', borderRadius: 6, padding: '8px 10px',
  fontSize: 13, color: '#1a2a4a', width: '100%', boxSizing: 'border-box',
  outline: 'none', background: '#fff',
};

const btnPrimary = {
  background: '#1565C0', color: '#fff', border: 'none',
  borderRadius: 7, padding: '9px 18px', fontSize: 13,
  fontWeight: 600, cursor: 'pointer',
};

const btnSecondary = {
  background: '#fff', color: '#1565C0', border: '1.5px solid #1565C0',
  borderRadius: 7, padding: '9px 18px', fontSize: 13,
  fontWeight: 600, cursor: 'pointer',
};

const btnDanger = {
  background: '#c62828', color: '#fff', border: 'none',
  borderRadius: 7, padding: '9px 18px', fontSize: 13,
  fontWeight: 600, cursor: 'pointer',
};

// ── Composants utilitaires ────────────────────────────────────

function RoleBadge({ role }) {
  if (!role) return null;
  return (
    <span style={{
      background: role.bg_couleur || '#eceff1',
      color: role.couleur || '#546e7a',
      padding: '3px 10px', borderRadius: 20,
      fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
    }}>
      {role.label}
    </span>
  );
}

function Toast({ msg, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3200); return () => clearTimeout(t); }, [onClose]);
  return (
    <div style={{
      position: 'fixed', bottom: 28, right: 28, zIndex: 9999,
      background: type === 'error' ? '#c62828' : '#2e7d32',
      color: '#fff', padding: '12px 22px', borderRadius: 8,
      boxShadow: '0 4px 16px rgba(0,0,0,0.18)', fontSize: 14, fontWeight: 600,
    }}>{msg}</div>
  );
}

function Alert({ type, msg, onClose }) {
  if (!msg) return null;
  const cfg = {
    success: { bg: '#e8f5e9', color: '#1b5e20', border: '#2e7d32' },
    error:   { bg: '#ffebee', color: '#b71c1c', border: '#c62828' },
    info:    { bg: '#e3f2fd', color: '#0d47a1', border: '#1565C0' },
  }[type] || {};
  return (
    <div style={{
      background: cfg.bg, color: cfg.color, borderLeft: `4px solid ${cfg.border}`,
      padding: '10px 16px', borderRadius: 6, display: 'flex',
      alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, fontSize: 14,
    }}>
      <span>{msg}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: cfg.color, fontWeight: 700, fontSize: 18 }}>×</button>
    </div>
  );
}

function Field({ label, required, children, error }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: '#546e7a', textTransform: 'uppercase', letterSpacing: '.4px' }}>
        {label}{required && <span style={{ color: '#c62828', marginLeft: 2 }}>*</span>}
      </label>
      {children}
      {error && <span style={{ fontSize: 11, color: '#c62828' }}>{error}</span>}
    </div>
  );
}

function Modal({ title, onClose, children, width = 560 }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: '#fff', borderRadius: 12, width: '100%', maxWidth: width,
        maxHeight: '90vh', overflow: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: '1px solid #e0e7ef',
          position: 'sticky', top: 0, background: '#fff', zIndex: 1,
        }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1a2a4a' }}>{title}</h3>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#90a4ae',
          }}>×</button>
        </div>
        <div style={{ padding: 20 }}>{children}</div>
      </div>
    </div>
  );
}

// ── Formulaire création / édition de rôle ─────────────────────

function RoleForm({ role, onSave, onClose, saving }) {
  const isEdit = !!role;
  const [form, setForm] = useState({
    code:        role?.code        || '',
    label:       role?.label       || '',
    description: role?.description || '',
    couleur:     role?.couleur     || '#546e7a',
    bg_couleur:  role?.bg_couleur  || '#eceff1',
    icone:       role?.icone       || 'shield',
  });
  const [errors, setErrors] = useState({});

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErrors(e => ({ ...e, [k]: '' })); };

  const autoCode = (label) => {
    const code = label.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    set('code', code);
  };

  const validate = () => {
    const e = {};
    if (!form.label.trim()) e.label = 'Obligatoire';
    if (!form.code.trim())  e.code  = 'Obligatoire';
    if (!/^[a-z0-9_]{2,60}$/.test(form.code)) e.code = 'Lettres minuscules, chiffres et _ uniquement (2-60 car.)';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => { if (validate()) onSave(form); };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Libellé du rôle" required error={errors.label}>
          <input
            style={{ ...inputStyle, borderColor: errors.label ? '#c62828' : '#cfd8dc' }}
            value={form.label}
            onChange={e => { set('label', e.target.value); if (!isEdit) autoCode(e.target.value); }}
            placeholder="Ex : Superviseur paie"
          />
        </Field>
        <Field label="Code interne" required error={errors.code}>
          <input
            style={{ ...inputStyle, fontFamily: 'monospace', borderColor: errors.code ? '#c62828' : '#cfd8dc' }}
            value={form.code}
            onChange={e => set('code', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
            placeholder="superviseur_paie"
            disabled={isEdit && role.est_systeme}
          />
        </Field>
      </div>

      <Field label="Description">
        <textarea
          style={{ ...inputStyle, resize: 'vertical', minHeight: 68 }}
          value={form.description}
          onChange={e => set('description', e.target.value)}
          placeholder="Périmètre, responsabilités, cas d'usage…"
        />
      </Field>

      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#546e7a', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 8 }}>
          Couleur du badge
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {COULEURS_PRESET.map(c => (
            <div
              key={c.couleur}
              title={c.couleur}
              onClick={() => { set('couleur', c.couleur); set('bg_couleur', c.bg); }}
              style={{
                width: 30, height: 30, borderRadius: '50%', background: c.couleur,
                cursor: 'pointer', border: form.couleur === c.couleur ? '3px solid #1a2a4a' : '3px solid transparent',
                boxSizing: 'border-box', transition: 'border-color .15s',
              }}
            />
          ))}
        </div>
        <div style={{ marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, color: '#546e7a' }}>Aperçu :</span>
          <span style={{
            background: form.bg_couleur, color: form.couleur,
            padding: '3px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700,
          }}>{form.label || 'Nouveau rôle'}</span>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingTop: 4 }}>
        <button onClick={onClose} style={btnSecondary}>Annuler</button>
        <button onClick={handleSubmit} disabled={saving} style={{ ...btnPrimary, opacity: saving ? .7 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}>
          {saving ? '⏳ Enregistrement…' : isEdit ? '💾 Mettre à jour' : '✅ Créer le rôle'}
        </button>
      </div>
    </div>
  );
}

// ── Matrice des permissions (pour un rôle) ────────────────────

function PermissionsMatrix({ roleId, roleName, isSysteme, onSaved }) {
  const [perms,   setPerms]   = useState({});
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [changed, setChanged] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/roles/${roleId}/permissions`)
      .then(res => {
        const d = res.data;
        setPerms(Array.isArray(d) ? d : (d?.data || d || {}));
      })
      .catch(() => setPerms({}))
      .finally(() => setLoading(false));
  }, [roleId]);

  const toggle = (module, action) => {
    setPerms(prev => {
      const curr = prev[module] || [];
      const next  = curr.includes(action)
        ? curr.filter(a => a !== action)
        : [...curr, action];
      return { ...prev, [module]: next };
    });
    setChanged(true);
  };

  const toggleModule = (module) => {
    const actionsDispos = ACTIONS_PAR_MODULE[module] || [];
    const currActions   = perms[module] || [];
    const toutCoche     = actionsDispos.every(a => currActions.includes(a));
    setPerms(prev => ({
      ...prev,
      [module]: toutCoche ? [] : [...actionsDispos],
    }));
    setChanged(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/roles/${roleId}/permissions`, perms);
      setChanged(false);
      onSaved && onSaved();
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: '24px 0', textAlign: 'center', color: '#90a4ae', fontSize: 13 }}>Chargement des permissions…</div>;

  const actionWidth = `${Math.floor(72 / TOUTES_ACTIONS.length)}%`;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <p style={{ margin: 0, fontSize: 13, color: '#546e7a' }}>
          Cliquez sur une cellule pour activer / désactiver une permission.
        </p>
        {changed && (
          <button onClick={handleSave} disabled={saving} style={{ ...btnPrimary, opacity: saving ? .7 : 1, padding: '7px 16px', fontSize: 12 }}>
            {saving ? '⏳ Sauvegarde…' : '💾 Sauvegarder'}
          </button>
        )}
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#f7f9fc' }}>
              <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #e0e7ef', color: '#546e7a', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', minWidth: 160 }}>
                Module
              </th>
              {TOUTES_ACTIONS.map(a => (
                <th key={a} style={{ padding: '8px 6px', textAlign: 'center', borderBottom: '2px solid #e0e7ef', color: '#546e7a', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', width: actionWidth }}>
                  {ACTIONS_LABELS[a]}
                </th>
              ))}
              <th style={{ padding: '8px 6px', textAlign: 'center', borderBottom: '2px solid #e0e7ef', color: '#546e7a', fontWeight: 700, fontSize: 11, textTransform: 'uppercase', width: '8%' }}>
                Tout
              </th>
            </tr>
          </thead>
          <tbody>
            {MODULES_ORDRE.map((module, idx) => {
              const actionsDispos = ACTIONS_PAR_MODULE[module] || [];
              const currActions   = perms[module] || [];
              const toutCoche     = actionsDispos.length > 0 && actionsDispos.every(a => currActions.includes(a));

              return (
                <tr key={module} style={{ background: idx % 2 === 0 ? '#fff' : '#fafbfd' }}>
                  <td style={{ padding: '10px 12px', fontWeight: 600, color: '#1a2a4a', borderBottom: '1px solid #f1f4f8' }}>
                    {MODULES_LABELS[module]}
                  </td>
                  {TOUTES_ACTIONS.map(action => {
                    const disponible = actionsDispos.includes(action);
                    const coche      = currActions.includes(action);
                    return (
                      <td key={action} style={{ padding: '10px 6px', textAlign: 'center', borderBottom: '1px solid #f1f4f8' }}>
                        {disponible ? (
                          <div
                            onClick={() => toggle(module, action)}
                            title={coche ? `Retirer "${ACTIONS_LABELS[action]}"` : `Accorder "${ACTIONS_LABELS[action]}"`}
                            style={{
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              width: 24, height: 24, borderRadius: 6,
                              background: coche ? '#e8f5e9' : '#f7f9fc',
                              border: `1.5px solid ${coche ? '#2e7d32' : '#cfd8dc'}`,
                              cursor: 'pointer',
                              transition: 'all .12s',
                              color: coche ? '#2e7d32' : '#cfd8dc',
                              fontSize: 15, fontWeight: 700,
                            }}
                          >
                            {coche ? '✓' : '·'}
                          </div>
                        ) : (
                          <span style={{ color: '#e0e7ef', fontSize: 16 }}>—</span>
                        )}
                      </td>
                    );
                  })}
                  <td style={{ padding: '10px 6px', textAlign: 'center', borderBottom: '1px solid #f1f4f8' }}>
                    <div
                      onClick={() => toggleModule(module)}
                      title={toutCoche ? 'Tout décocher' : 'Tout cocher'}
                      style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 24, height: 24, borderRadius: 6,
                        background: toutCoche ? '#e3f2fd' : '#f7f9fc',
                        border: `1.5px solid ${toutCoche ? '#1565C0' : '#cfd8dc'}`,
                        cursor: 'pointer',
                        color: toutCoche ? '#1565C0' : '#cfd8dc',
                        fontSize: 13, fontWeight: 700,
                      }}
                    >
                      {toutCoche ? '⊠' : '☐'}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Panneau affectation : liste utilisateurs + changement rôle ─

function AffectationPanel({ roles, onToast }) {
  const [users,   setUsers]   = useState([]);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [saving,  setSaving]  = useState(null); // id utilisateur en cours
  const [selection, setSelection] = useState([]); // ids sélectionnés (masse)
  const [massModal, setMassModal] = useState(false);
  const [massRole,  setMassRole]  = useState('');

  const LIMIT = 20;
  const searchRef = useRef();
  const pollingRef = useRef(null);

  const load = useCallback(async (p = 1, silent = false) => {
    if (!silent) setLoading(true);
    try {
      // Charger utilisateurs (avec compte) + tous les employés actifs en parallèle
      const [resU, resE] = await Promise.all([
        api.get('/utilisateurs', { params: { page: 1, limit: 1000, ...(search ? { q: search } : {}), ...(filterRole ? { role: filterRole } : {}) } }),
        api.get('/employes',     { params: { limit: 1000, statut: 'Actif' } }),
      ]);

      // Normaliser la liste utilisateurs
      const rawU    = resU.data;
      const payload = rawU?.data?.data !== undefined ? rawU.data : (rawU?.data || rawU);
      const userList = Array.isArray(payload) ? payload : (payload?.data || []);

      // Normaliser la liste employés
      const rawE    = resE.data;
      const empPayload = rawE?.data?.data !== undefined ? rawE.data : (rawE?.data || rawE);
      const empList = Array.isArray(empPayload) ? empPayload : (empPayload?.data || []);

      // Fusionner : les employés sans compte utilisateur apparaissent aussi
      const usersByEmpId = {};
      userList.forEach(u => { if (u.employe_id) usersByEmpId[u.employe_id] = u; });

      const merged = [];
      // D'abord tous les utilisateurs existants
      userList.forEach(u => merged.push(u));
      // Puis les employés sans compte (non encore dans userList)
      empList.forEach(e => {
        if (!usersByEmpId[e.id] && !userList.find(u => u.employe_id === e.id)) {
          merged.push({
            id:           null,
            employe_id:   e.id,
            nom:          e.nom,
            prenoms:      e.prenoms,
            email:        e.email || '',
            login:        e.matricule || `EMP-${String(e.id).padStart(5,'0')}`,
            role:         null,
            role_id:      null,
            statut:       e.statut || 'Actif',
            bureau_id:    e.bureau_id || null,
            bureau_nom:   e.bureau_nom || '',
            _sans_compte: true,
          });
        }
      });

      // Filtrage recherche côté client pour les sans-compte
      const q = search.toLowerCase();
      const list = q
        ? merged.filter(u =>
            (u.nom + ' ' + (u.prenoms || '')).toLowerCase().includes(q) ||
            (u.login || '').toLowerCase().includes(q) ||
            (u.email || '').toLowerCase().includes(q)
          )
        : merged;

      setUsers(list);
      setTotal(list.length);
      setPage(1);
    } catch {
      if (!silent) setUsers([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [search, filterRole]);

  // Chargement initial + polling 30s pour détecter les nouveaux employés
  useEffect(() => {
    load(1);
    pollingRef.current = setInterval(() => load(1, true), 30000);
    return () => clearInterval(pollingRef.current);
  }, [load]);

  const handleRoleChange = async (userId, roleId) => {
    setSaving(userId);
    try {
      await api.put(`/utilisateurs/${userId}/role`, { role_id: roleId });
      const r = roles.find(r => r.id === parseInt(roleId));
      onToast(`Rôle « ${r?.label} » affecté ✓`);
      load(page);
    } catch (err) {
      onToast(err.response?.data?.message || 'Erreur affectation', 'error');
    } finally {
      setSaving(null);
    }
  };

  const handleMassAffect = async () => {
    if (!massRole || selection.length === 0) return;
    setSaving('mass');
    try {
      await api.post('/utilisateurs/affecter-role', {
        role_id: parseInt(massRole),
        utilisateur_ids: selection,
      });
      const r = roles.find(r => r.id === parseInt(massRole));
      onToast(`${selection.length} utilisateur(s) → « ${r?.label} » ✓`);
      setSelection([]);
      setMassModal(false);
      load(page);
    } catch (err) {
      onToast(err.response?.data?.message || 'Erreur affectation en masse', 'error');
    } finally {
      setSaving(null);
    }
  };

  const toggleSelect = (id) => setSelection(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const toggleAll    = () => setSelection(s => s.length === users.length ? [] : users.map(u => u.id));

  // Créer automatiquement les comptes manquants pour les employés sans compte
  const [creating, setCreating] = useState(null); // employe_id en cours
  const handleCreerCompte = async (emp) => {
    setCreating(emp.employe_id);
    try {
      const roleLecteur = roles.find(r => r.code === 'lecteur' || r.label?.toLowerCase() === 'lecteur');
      await api.post('/utilisateurs', {
        login:                  emp.login,           // matricule
        nom:                    emp.nom,
        prenoms:                emp.prenoms || '',
        email:                  emp.email || '',
        telephone:              '',
        role:                   roleLecteur?.code || 'lecteur',
        role_id:                roleLecteur?.id   || null,
        statut:                 'Actif',
        bureau_id:              emp.bureau_id      || null,
        employe_id:             emp.employe_id,
        mot_de_passe:           'Admin123',
        force_changement_mdp:   true,
      });
      onToast(`Compte créé pour ${emp.nom} ${emp.prenoms || ''} ✓`);
      load(1, true);
    } catch (err) {
      onToast(err.response?.data?.message || 'Erreur création compte', 'error');
    } finally {
      setCreating(null);
    }
  };

  // Créer tous les comptes manquants en masse
  const [creatingAll, setCreatingAll] = useState(false);
  const handleCreerTousLesComptes = async () => {
    const sansCo = users.filter(u => u._sans_compte);
    if (sansCo.length === 0) return;
    setCreatingAll(true);
    const roleLecteur = roles.find(r => r.code === 'lecteur' || r.label?.toLowerCase() === 'lecteur');
    let ok = 0, ko = 0;
    for (const emp of sansCo) {
      try {
        await api.post('/utilisateurs', {
          login:               emp.login,
          nom:                 emp.nom,
          prenoms:             emp.prenoms || '',
          email:               emp.email || '',
          telephone:           '',
          role:                roleLecteur?.code || 'lecteur',
          role_id:             roleLecteur?.id   || null,
          statut:              'Actif',
          bureau_id:           emp.bureau_id      || null,
          employe_id:          emp.employe_id,
          mot_de_passe:        'Admin123',
          force_changement_mdp: true,
        });
        ok++;
      } catch { ko++; }
    }
    onToast(`${ok} compte(s) créé(s)${ko ? ` — ${ko} erreur(s)` : ''} ✓`);
    setCreatingAll(false);
    load(1);
  };

  return (
    <div>
      {/* Barre recherche + filtres */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          ref={searchRef}
          style={{ ...inputStyle, flex: 1, minWidth: 200 }}
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="🔍 Rechercher par nom, login, email…"
        />
        <select
          style={{ ...inputStyle, width: 180 }}
          value={filterRole}
          onChange={e => setFilterRole(e.target.value)}
        >
          <option value="">Tous les rôles</option>
          {roles.map(r => <option key={r.id} value={r.code}>{r.label}</option>)}
        </select>
        {selection.length > 0 && (
          <button onClick={() => setMassModal(true)} style={{ ...btnPrimary, whiteSpace: 'nowrap' }}>
            ⚡ Affecter {selection.length} sélectionné(s)
          </button>
        )}
        {users.filter(u => u._sans_compte).length > 0 && (
          <button
            onClick={handleCreerTousLesComptes}
            disabled={creatingAll}
            style={{ whiteSpace:'nowrap', background: creatingAll ? '#e5e7eb' : '#7c3aed', color: creatingAll ? '#9ca3af' : '#fff', border:'none', borderRadius:6, padding:'0 14px', height:36, fontWeight:600, fontSize:13, cursor: creatingAll ? 'not-allowed' : 'pointer' }}
          >
            {creatingAll ? '⏳ Création…' : `🪄 Créer ${users.filter(u => u._sans_compte).length} compte(s) manquant(s)`}
          </button>
        )}
      </div>

      {/* Tableau */}
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e0e7ef', overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: '32px', textAlign: 'center', color: '#90a4ae', fontSize: 13 }}>Chargement…</div>
        ) : users.length === 0 ? (
          <div style={{ padding: '32px', textAlign: 'center', color: '#90a4ae', fontStyle: 'italic' }}>Aucun utilisateur trouvé</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ padding: '10px 14px', textAlign: 'center', fontSize: 11, textTransform: 'uppercase', color: '#546e7a', borderBottom: '2px solid #e0e7ef', width: 40 }}>
                  <input type="checkbox" checked={selection.length === users.length && users.length > 0} onChange={toggleAll} style={{ cursor: 'pointer' }} />
                </th>
                {['Utilisateur', 'Login', 'Rôle actuel', 'Statut', 'Changer le rôle'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.4px', color: '#546e7a', borderBottom: '2px solid #e0e7ef', fontWeight: 700 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map(u => {
                const roleActuel = roles.find(r => r.id === u.role_id || r.code === u.role);
                const isSaving   = saving === u.id;
                const initiale   = (u.nom?.[0] || '?').toUpperCase();
                const rowKey = u.id ? `u-${u.id}` : `e-${u.employe_id}`;
                return (
                  <tr key={rowKey} style={{ background: selection.includes(u.id) ? '#e3f2fd' : u._sans_compte ? '#fafbfc' : 'transparent' }}>
                    <td style={{ padding: '10px 14px', textAlign: 'center', borderBottom: '1px solid #f1f4f8' }}>
                      {u.id && <input type="checkbox" checked={selection.includes(u.id)} onChange={() => toggleSelect(u.id)} style={{ cursor: 'pointer' }} />}
                    </td>
                    <td style={{ padding: '10px 14px', borderBottom: '1px solid #f1f4f8' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                          width: 34, height: 34, borderRadius: '50%',
                          background: u._sans_compte ? '#f3f4f6' : (roleActuel?.bg_couleur || '#eceff1'),
                          color: u._sans_compte ? '#9ca3af' : (roleActuel?.couleur || '#546e7a'),
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontWeight: 800, fontSize: 14, flexShrink: 0,
                        }}>{initiale}</div>
                        <div>
                          <div style={{ fontWeight: 600, color: '#1a2a4a' }}>{u.nom} {u.prenoms || ''}</div>
                          <div style={{ fontSize: 11, color: '#90a4ae' }}>
                            {u.email || ''}
                            {u._sans_compte && <span style={{ marginLeft:6, background:'#fef9c3', color:'#92400e', padding:'1px 7px', borderRadius:10, fontSize:10, fontWeight:700 }}>Sans compte</span>}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '10px 14px', borderBottom: '1px solid #f1f4f8', fontFamily: 'monospace', color: u._sans_compte ? '#d1d5db' : '#546e7a', fontSize: 12 }}>
                      {u._sans_compte ? <span style={{ color:'#d1d5db', fontStyle:'italic' }}>—</span> : `@${u.login}`}
                    </td>
                    <td style={{ padding: '10px 14px', borderBottom: '1px solid #f1f4f8' }}>
                      {roleActuel ? <RoleBadge role={roleActuel} /> : <span style={{ color: '#cfd8dc' }}>—</span>}
                    </td>
                    <td style={{ padding: '10px 14px', borderBottom: '1px solid #f1f4f8' }}>
                      <span style={{
                        display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                        background: u.statut === 'Actif' ? '#e8f5e9' : u.statut === 'Suspendu' ? '#fff3e0' : '#eceff1',
                        color: u.statut === 'Actif' ? '#2e7d32' : u.statut === 'Suspendu' ? '#e65100' : '#546e7a',
                      }}>{u.statut}</span>
                    </td>
                    <td style={{ padding: '10px 14px', borderBottom: '1px solid #f1f4f8' }}>
                      {u._sans_compte ? (
                        <button
                          onClick={() => handleCreerCompte(u)}
                          disabled={creating === u.employe_id || creatingAll}
                          style={{ background: creating === u.employe_id ? '#e5e7eb' : '#ede9fe', color: creating === u.employe_id ? '#9ca3af' : '#7c3aed', border:'1px solid #c4b5fd', borderRadius:6, padding:'4px 12px', fontSize:12, fontWeight:600, cursor:'pointer' }}
                        >
                          {creating === u.employe_id ? '⏳ Création…' : '➕ Créer le compte'}
                        </button>
                      ) : (
                        <>
                          <select
                            style={{ ...inputStyle, width: 'auto', minWidth: 160, opacity: isSaving ? .6 : 1 }}
                            value={u.role_id || ''}
                            disabled={isSaving}
                            onChange={e => handleRoleChange(u.id, e.target.value)}
                          >
                            <option value="">— Choisir —</option>
                            {roles.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
                          </select>
                          {isSaving && <span style={{ marginLeft: 8, fontSize: 12, color: '#90a4ae' }}>⏳</span>}
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Compteur total */}
        <div style={{ padding: '10px 16px', borderTop: '1px solid #f1f4f8', fontSize: 12, color: '#90a4ae', display:'flex', alignItems:'center', gap:6 }}>
          <span style={{ width:7, height:7, borderRadius:'50%', background:'#4caf50', display:'inline-block' }} />
          {total} utilisateur(s) au total — actualisation automatique toutes les 30s
        </div>
      </div>

      {/* Modal affectation en masse */}
      {massModal && (
        <Modal title={`⚡ Affecter un rôle à ${selection.length} utilisateur(s)`} onClose={() => setMassModal(false)} width={440}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p style={{ margin: 0, fontSize: 13, color: '#546e7a', lineHeight: 1.6 }}>
              Le rôle sélectionné sera appliqué aux <strong>{selection.length}</strong> utilisateur(s) cochés.
              Cette action est immédiate et enregistrée dans l'audit.
            </p>
            <Field label="Nouveau rôle" required>
              <select style={inputStyle} value={massRole} onChange={e => setMassRole(e.target.value)}>
                <option value="">— Choisir un rôle —</option>
                {roles.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
            </Field>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setMassModal(false)} style={btnSecondary}>Annuler</button>
              <button onClick={handleMassAffect} disabled={!massRole || saving === 'mass'} style={{ ...btnPrimary, opacity: (!massRole || saving === 'mass') ? .6 : 1, cursor: (!massRole || saving === 'mass') ? 'not-allowed' : 'pointer' }}>
                {saving === 'mass' ? '⏳ Traitement…' : '⚡ Appliquer'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── Journal d'audit des droits ─────────────────────────────────

function AuditPanel() {
  const [logs,    setLogs]    = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/roles/audit', { params: { limit: 50 } })
      .then(res => {
        const d = res.data;
        setLogs(Array.isArray(d) ? d : (d?.data || []));
      })
      .catch(() => setLogs([]))
      .finally(() => setLoading(false));
  }, []);

  const ACTION_LABELS = {
    creer_role:          { label: 'Rôle créé',               color: '#1565C0', bg: '#e3f2fd' },
    modifier_role:       { label: 'Rôle modifié',            color: '#e65100', bg: '#fff3e0' },
    supprimer_role:      { label: 'Rôle supprimé',           color: '#c62828', bg: '#ffebee' },
    modifier_permission: { label: 'Permission modifiée',     color: '#4527a0', bg: '#ede7f6' },
    affecter_role:       { label: 'Rôle affecté',            color: '#2e7d32', bg: '#e8f5e9' },
  };

  return (
    <div>
      {loading ? (
        <div style={{ padding: '32px', textAlign: 'center', color: '#90a4ae', fontSize: 13 }}>Chargement du journal…</div>
      ) : logs.length === 0 ? (
        <div style={{ padding: '32px', textAlign: 'center', color: '#90a4ae', fontStyle: 'italic' }}>Aucune entrée dans le journal</div>
      ) : (
        <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e0e7ef', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f7f9fc' }}>
                {['Date', 'Opérateur', 'Action', 'Cible', 'IP'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.4px', color: '#546e7a', borderBottom: '2px solid #e0e7ef', fontWeight: 700 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {logs.map((l, i) => {
                const cfg = ACTION_LABELS[l.action] || { label: l.action, color: '#546e7a', bg: '#eceff1' };
                return (
                  <tr key={i} style={{ borderBottom: '1px solid #f1f4f8' }}>
                    <td style={{ padding: '10px 14px', color: '#90a4ae', whiteSpace: 'nowrap', fontSize: 12 }}>{fmtDate(l.created_at)}</td>
                    <td style={{ padding: '10px 14px', fontWeight: 600, color: '#1a2a4a' }}>{l.operateur_nom || <span style={{ color: '#cfd8dc' }}>Système</span>}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ background: cfg.bg, color: cfg.color, padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700 }}>{cfg.label}</span>
                    </td>
                    <td style={{ padding: '10px 14px', color: '#546e7a', fontSize: 12 }}>{l.cible_type} #{l.cible_id || '—'}</td>
                    <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontSize: 12, color: '#90a4ae' }}>{l.ip_address || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Page principale ───────────────────────────────────────────

const TABS = [
  { key: 'roles',      label: '🛡️ Rôles' },
  { key: 'matrice',    label: '🔲 Matrice des droits' },
  { key: 'affectation', label: '👥 Affectation' },
  { key: 'audit',      label: '📋 Journal d\'audit' },
];

export default function RolesPage() {
  const [tab,     setTab]     = useState('roles');
  const [roles,   setRoles]   = useState([]);
  const [stats,   setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [toast,   setToast]   = useState(null);
  const [alert,   setAlert]   = useState(null);

  // Rôle sélectionné pour la matrice
  const [selectedRoleId, setSelectedRoleId] = useState(null);

  // Modals
  const [showCreate, setShowCreate] = useState(false);
  const [editRole,   setEditRole]   = useState(null);
  const [deleteRole, setDeleteRole] = useState(null);
  const [dupeRole,   setDupeRole]   = useState(null);
  const [dupeForm,   setDupeForm]   = useState({ code: '', label: '' });

  const showToast = (msg, type = 'success') => setToast({ msg, type });
  const showAlert = (type, msg) => setAlert({ type, msg });

  // ── Chargement ───────────────────────────────────────────────

  const loadRoles = useCallback(async () => {
    setLoading(true);
    try {
      const [rolesRes, statsRes] = await Promise.all([
        api.get('/roles'),
        api.get('/roles/stats'),
      ]);
      const data = Array.isArray(rolesRes.data) ? rolesRes.data : (rolesRes.data?.data || []);
      setRoles(data);
      const statsRaw = statsRes.data;
      setStats(statsRaw?.data || statsRaw || null);
      if (data.length > 0 && !selectedRoleId) setSelectedRoleId(data[0].id);
    } catch {
      showAlert('error', 'Erreur lors du chargement des rôles.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadRoles(); }, [loadRoles]);

  // ── CRUD ─────────────────────────────────────────────────────

  const handleCreate = async (payload) => {
    setSaving(true);
    try {
      await api.post('/roles', payload);
      showToast(`Rôle « ${payload.label} » créé avec succès ✓`);
      setShowCreate(false);
      loadRoles();
    } catch (err) {
      showAlert('error', err.response?.data?.message || 'Erreur lors de la création.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (payload) => {
    setSaving(true);
    try {
      await api.put(`/roles/${editRole.id}`, payload);
      showToast(`Rôle « ${payload.label} » mis à jour ✓`);
      setEditRole(null);
      loadRoles();
    } catch (err) {
      showAlert('error', err.response?.data?.message || 'Erreur lors de la mise à jour.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await api.delete(`/roles/${deleteRole.id}`);
      showToast(`Rôle « ${deleteRole.label} » supprimé ✓`);
      setDeleteRole(null);
      if (selectedRoleId === deleteRole.id) setSelectedRoleId(null);
      loadRoles();
    } catch (err) {
      showAlert('error', err.response?.data?.message || 'Suppression impossible.');
      setDeleteRole(null);
    } finally {
      setSaving(false);
    }
  };

  const handleDupliquer = async () => {
    if (!dupeForm.code || !dupeForm.label) return;
    setSaving(true);
    try {
      await api.post(`/roles/dupliquer/${dupeRole.id}`, dupeForm);
      showToast(`Rôle « ${dupeForm.label} » créé par duplication ✓`);
      setDupeRole(null);
      loadRoles();
    } catch (err) {
      showAlert('error', err.response?.data?.message || 'Erreur duplication.');
    } finally {
      setSaving(false);
    }
  };

  // ── Rendu ─────────────────────────────────────────────────────

  const selectedRole = roles.find(r => r.id === selectedRoleId);

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1200, margin: '0 auto' }}>
      {/* En-tête */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a2a4a' }}>🛡️ Rôles &amp; droits d'accès</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#90a4ae' }}>Gestion des permissions par module — GFM3 RH</p>
        </div>
        <button onClick={() => setShowCreate(true)} style={btnPrimary}>
          + Nouveau rôle
        </button>
      </div>

      <Alert type={alert?.type} msg={alert?.msg} onClose={() => setAlert(null)} />

      {/* Statistiques */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24 }}>
          {[
            { label: 'Rôles définis',       val: stats.global?.nb_roles,             color: '#4a148c', bg: '#f3e5f5' },
            { label: 'Permissions actives',  val: stats.global?.nb_permissions,       color: '#1565C0', bg: '#e3f2fd' },
            { label: 'Utilisateurs actifs',  val: stats.global?.nb_utilisateurs,      color: '#2e7d32', bg: '#e8f5e9' },
            { label: 'Modules couverts',     val: stats.global?.nb_modules_couverts,  color: '#e65100', bg: '#fff3e0' },
          ].map(s => (
            <div key={s.label} style={{ background: s.bg, borderRadius: 10, padding: '14px 18px' }}>
              <div style={{ fontSize: 26, fontWeight: 700, color: s.color }}>{s.val ?? '—'}</div>
              <div style={{ fontSize: 12, color: s.color, marginTop: 2, opacity: .8 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Onglets */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #e0e7ef', marginBottom: 24 }}>
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '10px 20px', border: 'none', background: 'none',
              fontSize: 13, fontWeight: tab === t.key ? 700 : 400,
              color: tab === t.key ? '#1565C0' : '#607d8b',
              borderBottom: tab === t.key ? '3px solid #1565C0' : '3px solid transparent',
              cursor: 'pointer', marginBottom: -2, transition: 'all .15s',
            }}
          >{t.label}</button>
        ))}
      </div>

      {/* ── Onglet : RÔLES ─────────────────────────────────── */}
      {tab === 'roles' && (
        <div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '48px', color: '#90a4ae', fontSize: 13 }}>Chargement des rôles…</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {roles.map(role => (
                <div key={role.id} style={{
                  background: '#fff', border: '1px solid #e0e7ef', borderRadius: 10,
                  padding: '16px 20px', display: 'grid',
                  gridTemplateColumns: '48px 1fr auto', gap: 16, alignItems: 'start',
                  transition: 'box-shadow .15s',
                }}>
                  {/* Icône */}
                  <div style={{
                    width: 48, height: 48, borderRadius: 10,
                    background: role.bg_couleur, color: role.couleur,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 22, flexShrink: 0,
                  }}>
                    {{ crown: '👑', 'shield-check': '🛡️', users: '👥', briefcase: '💼', eye: '👁️', shield: '🔰' }[role.icone] || '🔐'}
                  </div>

                  {/* Info */}
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                      <span style={{ fontWeight: 700, fontSize: 15, color: '#1a2a4a' }}>{role.label}</span>
                      <RoleBadge role={role} />
                      {role.est_systeme === 1 && (
                        <span style={{ fontSize: 10, background: '#f7f9fc', color: '#90a4ae', border: '1px solid #e0e7ef', padding: '1px 6px', borderRadius: 4, fontWeight: 600 }}>SYSTÈME</span>
                      )}
                      {!role.actif && (
                        <span style={{ fontSize: 10, background: '#ffebee', color: '#c62828', padding: '1px 6px', borderRadius: 4, fontWeight: 600 }}>INACTIF</span>
                      )}
                    </div>
                    <p style={{ margin: '0 0 8px', fontSize: 13, color: '#546e7a', lineHeight: 1.5 }}>{role.description || '—'}</p>
                    <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#90a4ae' }}>
                      <span>🔑 <strong style={{ color: '#1a2a4a' }}>{role.nb_permissions}</strong> permissions</span>
                      <span>👤 <strong style={{ color: '#1a2a4a' }}>{role.nb_utilisateurs}</strong> utilisateur(s) actif(s)</span>
                      <span>📅 Créé le {fmtDate(role.created_at)}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <button
                      title="Modifier les permissions"
                      onClick={() => { setSelectedRoleId(role.id); setTab('matrice'); }}
                      style={{ padding: '7px 14px', border: '1px solid #e0e7ef', borderRadius: 7, background: '#f7f9fc', cursor: 'pointer', fontSize: 12, color: '#1565C0', fontWeight: 600 }}
                    >
                      🔲 Droits
                    </button>
                    {!role.est_systeme && (
                      <>
                        <button
                          title="Modifier le rôle"
                          onClick={() => setEditRole(role)}
                          style={{ padding: '7px 14px', border: '1px solid #e0e7ef', borderRadius: 7, background: '#f7f9fc', cursor: 'pointer', fontSize: 12, color: '#546e7a', fontWeight: 600 }}
                        >✏️</button>
                        <button
                          title="Dupliquer"
                          onClick={() => { setDupeRole(role); setDupeForm({ code: role.code + '_copie', label: 'Copie de ' + role.label }); }}
                          style={{ padding: '7px 14px', border: '1px solid #e0e7ef', borderRadius: 7, background: '#f7f9fc', cursor: 'pointer', fontSize: 12, color: '#546e7a', fontWeight: 600 }}
                        >📋</button>
                        <button
                          title="Supprimer"
                          onClick={() => setDeleteRole(role)}
                          style={{ padding: '7px 14px', border: '1px solid #ffcdd2', borderRadius: 7, background: '#fff', cursor: 'pointer', fontSize: 12, color: '#c62828', fontWeight: 600 }}
                        >🗑️</button>
                      </>
                    )}
                    {role.est_systeme && (
                      <button
                        title="Dupliquer (rôles système non modifiables)"
                        onClick={() => { setDupeRole(role); setDupeForm({ code: role.code + '_custom', label: role.label + ' (personnalisé)' }); }}
                        style={{ padding: '7px 14px', border: '1px solid #e0e7ef', borderRadius: 7, background: '#f7f9fc', cursor: 'pointer', fontSize: 12, color: '#546e7a', fontWeight: 600 }}
                      >📋 Dupliquer</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Onglet : MATRICE DES DROITS ────────────────────── */}
      {tab === 'matrice' && (
        <div>
          {/* Sélecteur de rôle */}
          <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #e0e7ef', marginBottom: 20, overflowX: 'auto' }}>
            {roles.map(r => (
              <button
                key={r.id}
                onClick={() => setSelectedRoleId(r.id)}
                style={{
                  padding: '8px 18px', border: 'none', background: 'none',
                  fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap',
                  fontWeight: selectedRoleId === r.id ? 700 : 400,
                  color: selectedRoleId === r.id ? r.couleur : '#607d8b',
                  borderBottom: selectedRoleId === r.id ? `3px solid ${r.couleur}` : '3px solid transparent',
                  marginBottom: -1,
                }}
              >{r.label}</button>
            ))}
          </div>

          {selectedRole ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <RoleBadge role={selectedRole} />
                <span style={{ fontSize: 13, color: '#546e7a' }}>{selectedRole.description}</span>
              </div>
              <PermissionsMatrix
                key={selectedRoleId}
                roleId={selectedRoleId}
                roleName={selectedRole.label}
                isSysteme={selectedRole.est_systeme === 1}
                onSaved={() => { showToast('Permissions sauvegardées ✓'); loadRoles(); }}
              />
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '48px', color: '#90a4ae', fontStyle: 'italic' }}>
              Sélectionnez un rôle pour afficher la matrice des droits.
            </div>
          )}
        </div>
      )}

      {/* ── Onglet : AFFECTATION ───────────────────────────── */}
      {tab === 'affectation' && (
        <AffectationPanel roles={roles} onToast={showToast} />
      )}

      {/* ── Onglet : AUDIT ─────────────────────────────────── */}
      {tab === 'audit' && <AuditPanel />}

      {/* ── Modals ────────────────────────────────────────── */}

      {showCreate && (
        <Modal title="➕ Nouveau rôle" onClose={() => setShowCreate(false)} width={560}>
          <RoleForm onSave={handleCreate} onClose={() => setShowCreate(false)} saving={saving} />
        </Modal>
      )}

      {editRole && (
        <Modal title={`✏️ Modifier — ${editRole.label}`} onClose={() => setEditRole(null)} width={560}>
          <RoleForm role={editRole} onSave={handleUpdate} onClose={() => setEditRole(null)} saving={saving} />
        </Modal>
      )}

      {deleteRole && (
        <Modal title="🗑️ Supprimer ce rôle ?" onClose={() => setDeleteRole(null)} width={440}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <p style={{ margin: 0, fontSize: 14, color: '#37474f', lineHeight: 1.6 }}>
              Vous êtes sur le point de supprimer le rôle <strong>« {deleteRole.label} »</strong>.
              Cette action est irréversible. Les utilisateurs ayant ce rôle le perdront.
            </p>
            <div style={{ background: '#ffebee', borderRadius: 6, padding: '10px 14px', fontSize: 13, color: '#c62828' }}>
              ⚠️ Impossible si des utilisateurs actifs sont encore affectés à ce rôle.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setDeleteRole(null)} style={btnSecondary}>Annuler</button>
              <button onClick={handleDelete} disabled={saving} style={{ ...btnDanger, opacity: saving ? .7 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}>
                {saving ? '⏳…' : '🗑️ Supprimer définitivement'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {dupeRole && (
        <Modal title={`📋 Dupliquer « ${dupeRole.label} »`} onClose={() => setDupeRole(null)} width={480}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <p style={{ margin: 0, fontSize: 13, color: '#546e7a', lineHeight: 1.6 }}>
              Un nouveau rôle sera créé avec les mêmes permissions que <strong>« {dupeRole.label} »</strong>.
            </p>
            <Field label="Libellé du nouveau rôle" required>
              <input style={inputStyle} value={dupeForm.label} onChange={e => setDupeForm(f => ({ ...f, label: e.target.value }))} />
            </Field>
            <Field label="Code interne" required>
              <input style={{ ...inputStyle, fontFamily: 'monospace' }} value={dupeForm.code} onChange={e => setDupeForm(f => ({ ...f, code: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') }))} />
            </Field>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setDupeRole(null)} style={btnSecondary}>Annuler</button>
              <button onClick={handleDupliquer} disabled={saving} style={{ ...btnPrimary, opacity: saving ? .7 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}>
                {saving ? '⏳…' : '📋 Dupliquer'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Toast */}
      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}
